import { AnchorButton, Button, ButtonGroup, Intent, Menu, MenuItem, Popover } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as Sentry from '@sentry/browser'
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Client from '../../../client'
import config from '../../../config'
import { showFailure, showToast } from '../../../Toast'
import { getErrorString } from '../../../utils/serverErrorUtils'

interface Props {
  productId: number
  disabled?: boolean
  expressListProduct?: Record<string, unknown>
  variant?: 'drawer' | 'table'
  style?: React.CSSProperties
}

const stopRowClickPropagation = (e: React.MouseEvent) => e.stopPropagation()

const ActionsContainer: React.FC<{
  variant: 'drawer' | 'table'
  style?: React.CSSProperties
  children: React.ReactNode
}> = ({ variant, style, children }) => {
  if (variant === 'drawer') {
    return <>{children}</>
  }

  return (
    <span style={style} onClick={stopRowClickPropagation} onMouseDown={stopRowClickPropagation}>
      {children}
    </span>
  )
}

export const DraftProductListActions: React.FC<Props> = ({
  productId,
  disabled = false,
  expressListProduct,
  variant = 'table',
  style
}) => {
  const navigate = useNavigate()
  const listHref = `/products/new?pid=${productId}`
  const expressListHref = `/express_list/${productId}`
  const small = variant === 'table'

  const navigateToExpressListFromTable = async () => {
    try {
      // Table rows come from the normal v5 index payload, which omits express-list fields unless per_page=1.
      const res = await Client.v5ProductsIndex({ query: String(productId), draft: true, perPage: 1 })
      const product = res.data?.products?.[0]
      if (product?.id === productId && product.title) {
        navigate(expressListHref, { state: product })
      } else {
        showToast('Product not found', Intent.WARNING)
      }
    } catch (e) {
      showFailure(getErrorString(e, Sentry.captureException))
    }
  }

  const expressListMenuItem =
    expressListProduct?.title != null ? (
      <Link to={expressListHref} state={expressListProduct}>
        <MenuItem text="Express List" disabled={disabled} />
      </Link>
    ) : (
      <MenuItem text="Express List" onClick={navigateToExpressListFromTable} disabled={disabled} />
    )

  const expressListMenu = <Menu>{expressListMenuItem}</Menu>

  const listButton = (
    <AnchorButton intent={Intent.SUCCESS} disabled={disabled} href={listHref} small={small}>
      {/* TODO(product_inventory_unit): Improve disabled state when implementing product inventory tracking for all marketplaces and/or lister books */}
      <span className="action-text">List</span>
    </AnchorButton>
  )

  const expressListPopoverProps =
    variant === 'drawer'
      ? {
          // Drawer action bar is fixed to the viewport bottom, so there is no room below the caret.
          placement: 'top-end' as const
        }
      : {
          placement: 'bottom-end' as const,
          boundary: 'viewport' as const,
          // Default scrollParent + flip/preventOverflow opens this menu above the row.
          modifiers: { flip: { enabled: false }, preventOverflow: { enabled: false } }
        }

  const expressListPopover = (
    <Popover content={expressListMenu} {...expressListPopoverProps}>
      <Button intent={Intent.SUCCESS} icon={IconNames.CARET_DOWN} disabled={disabled} small={small} />
    </Popover>
  )

  const renderActions = () => {
    if (disabled) {
      return (
        <Button small={small} intent={Intent.SUCCESS} disabled>
          List
        </Button>
      )
    }

    if (!config.EXPRESS_LIST()) {
      if (variant === 'table') {
        return (
          <Link to={listHref}>
            <Button small intent={Intent.SUCCESS}>
              List
            </Button>
          </Link>
        )
      }

      return listButton
    }

    return (
      <ButtonGroup className="list-action-group">
        {listButton}
        {expressListPopover}
      </ButtonGroup>
    )
  }

  return (
    <ActionsContainer variant={variant} style={style}>
      {renderActions()}
    </ActionsContainer>
  )
}
